import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Clapperboard,
  Loader2,
  Check,
  Download,
  Pencil,
  Share2,
  ChevronDown,
  ChevronUp,
  Clock,
  Globe2,
  Layers,
  AlertCircle,
  Send,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { appFetch, appApiBaseUrl } from '@/shared/api/app-client'
import type {
  ApiResponse,
  ClippingJobResult,
  ClipCandidate,
  TranscriptSentence,
  ProjectDetail,
  Template,
  Variant,
} from '@/shared/api/app-types'
import { variantToClipCandidate } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'
import {
  renderClipToMp4,
  getClipFileName,
  ensureClipWorkspaceRoot,
  buildClipProjectForEditor,
  type ClipRenderConfig,
} from '@/features/app-shell/utils/clip-render'
import { triggerDownload } from '@/features/assistant/tools/export-tools'
import { WorkspacePickerDialog } from '@/features/app-shell/components/workspace-picker-dialog'
import { PublishClipDialog } from '@/features/app-shell/components/publish-clip-dialog'
import { ClipRangePlayer } from './clip-results-canvas'

/* ------------------------------------------------------------------ */
/* 工具函数                                                            */
/* ------------------------------------------------------------------ */

function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return m > 0 ? `${m}分${s}秒` : `${s}秒`
}

function getSentencesInRange(
  sentences: TranscriptSentence[],
  startMs: number,
  endMs: number,
): TranscriptSentence[] {
  return sentences.filter(
    (s) => s.startMs <= endMs + 500 && s.endMs >= startMs - 500,
  )
}

function resolveSourceUrl(src: string): string {
  if (src.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${src}`
  }
  return src
}

/* ------------------------------------------------------------------ */
/* 片段卡片                                                            */
/* ------------------------------------------------------------------ */

interface PendingEdit {
  clip: ClipCandidate
  sentences: TranscriptSentence[]
  words: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }> | undefined
  variantId: number
}

interface PendingPublish {
  clip: ClipCandidate
  variant: Variant
}

function ClipCard({
  clip,
  index,
  variant,
  sentences,
  words,
  config,
  onEdit,
  editBusy,
  editError,
  onPublish,
}: {
  clip: ClipCandidate
  index: number
  variant: Variant
  sentences: TranscriptSentence[]
  words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>
  config: ClipRenderConfig
  onEdit: (clip: ClipCandidate, sentences: TranscriptSentence[], variantId: number, words?: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>) => void
  editBusy: boolean
  editError: string | null
  onPublish: (clip: ClipCandidate, variant: Variant) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [tab, setTab] = useState<'transcript' | 'caption'>('transcript')
  const [selected, setSelected] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const clipSentences = getSentencesInRange(sentences, clip.startMs, clip.endMs)
  const transcriptText = clipSentences.map((s) => s.text).join(' ')
  const durationSec = Math.round((clip.endMs - clip.startMs) / 1000)
  const scoreColor =
    clip.score >= 8
      ? 'text-emerald-500'
      : clip.score >= 6
        ? 'text-amber-500'
        : 'text-muted-foreground'

  const busy = editBusy || downloading
  const resolvedSrc = resolveSourceUrl(config.src)

  // 发布状态标签
  const published = variant.status === 'published'
  const statusBadge = published
    ? { label: '已发布', cls: 'bg-indigo-500/10 text-indigo-600' }
    : { label: '可分享', cls: 'bg-emerald-500/10 text-emerald-600' }

  async function handleDownload() {
    if (busy) return
    setDownloading(true)
    setDownloadError(null)
    setDownloadProgress(0)
    try {
      const result = await renderClipToMp4(clip, sentences, config, {
        onProgress: (progress) => {
          setDownloadProgress(progress.progress)
        },
        words,
      })
      const fileName = getClipFileName(clip)
      triggerDownload(result.blob, fileName)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : '渲染下载失败')
    } finally {
      setDownloading(false)
      setDownloadProgress(0)
    }
  }

  return (
    <div
      className={cn(
        'overflow-visible rounded-2xl border bg-card transition-colors',
        selected ? 'border-primary' : 'border-border',
      )}
    >
      {config.src ? (
        <div className="p-4 sm:p-5">
          <ClipRangePlayer
            src={resolvedSrc}
            startMs={clip.startMs}
            endMs={clip.endMs}
            sentences={clipSentences}
            config={config}
          />
        </div>
      ) : null}

      <div className="flex items-start gap-4 px-4 pb-3 sm:px-5">
        <button
          type="button"
          onClick={() => setSelected(!selected)}
          disabled={busy}
          className={cn(
            'mt-1 flex size-5 shrink-0 items-center justify-center rounded border transition-colors',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary',
          )}
        >
          {selected ? <Check className="size-3" /> : null}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-muted-foreground">#{index + 1}</span>
            <h3 className="truncate text-base font-semibold text-foreground">{clip.title}</h3>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="size-3" />{durationSec}秒</span>
            <span>·</span>
            <span>{formatMs(clip.startMs)} - {formatMs(clip.endMs)}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={cn('text-2xl font-bold', scoreColor)}>{clip.score.toFixed(1)}</div>
          <div className="text-[10px] text-muted-foreground">/10</div>
        </div>
      </div>

      <div className="px-4 pb-3 sm:px-5">
        <div className="text-xs font-medium text-muted-foreground">传播力评分理由：</div>
        <p className={cn('mt-1 text-sm leading-6 text-foreground/80', !expanded && 'line-clamp-2')}>
          {clip.reason}
        </p>
        {clip.reason.length > 60 ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-1 flex items-center gap-0.5 text-xs text-primary hover:underline"
          >
            {expanded ? <>收起 <ChevronUp className="size-3" /></> : <>展开 <ChevronDown className="size-3" /></>}
          </button>
        ) : null}
      </div>

      <div className="border-t border-border px-4 py-3 sm:px-5">
        <div className="flex gap-4 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => setTab('transcript')}
            className={cn('pb-1 text-xs font-medium transition-colors', tab === 'transcript' ? 'border-b-2 border-foreground text-foreground' : 'text-muted-foreground')}
          >
            转录文本
          </button>
          <button
            type="button"
            onClick={() => setTab('caption')}
            className={cn('pb-1 text-xs font-medium transition-colors', tab === 'caption' ? 'border-b-2 border-foreground text-foreground' : 'text-muted-foreground')}
          >
            社交文案
          </button>
        </div>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          {tab === 'transcript'
            ? transcriptText || '暂无转录文本'
            : `${clip.title} — ${clip.reason.slice(0, 30)}...`}
        </p>
      </div>

      {editError ? (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 sm:mx-5">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-xs text-destructive">{editError}</p>
        </div>
      ) : null}
      {downloadError ? (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 sm:mx-5">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p className="text-xs text-destructive">{downloadError}</p>
        </div>
      ) : null}
      <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
        <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium', statusBadge.cls)}>
          {published ? <Send className="size-3" /> : <Check className="size-3" />}
          {statusBadge.label}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => onEdit(clip, sentences, variant.id, words)} disabled={busy}>
            {editBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Pencil className="size-3.5" />}
            {editBusy ? '准备中…' : '编辑'}
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={handleDownload} disabled={busy}>
            {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
            {downloading ? `渲染 ${downloadProgress}%` : '下载'}
          </Button>
          <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => onPublish(clip, variant)} disabled={busy}>
            <Share2 className="size-3.5" />
            分享
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 主组件 — 项目详情页，展示 clip 结果                                 */
/* ------------------------------------------------------------------ */

export function VideoClipsCanvas({ projectId }: { projectId: string }) {
  const navigate = useNavigate()
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [clipVariants, setClipVariants] = useState<Variant[]>([])
  const [job, setJob] = useState<ClippingJobResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'score' | 'time'>('score')

  // 工作空间选择弹窗 + 编辑状态
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false)
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null)
  const [editBusy, setEditBusy] = useState(false)
  const [editingVariantId, setEditingVariantId] = useState<number | null>(null)
  const [editError, setEditError] = useState<string | null>(null)

  const [pendingPublish, setPendingPublish] = useState<PendingPublish | null>(null)
  const [brandTemplate, setBrandTemplate] = useState<Template | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        // 并行请求：项目详情 + variants（clip 列表）+ clipping job（仅取 transcript/config）
        const [projectRes, variantsRes, jobRes] = await Promise.allSettled([
          appFetch<ApiResponse<ProjectDetail>>(`/api/projects/${projectId}`),
          appFetch<ApiResponse<{ items: Variant[] }>>(`/api/projects/${projectId}/variants?variantType=clip`),
          appFetch<ApiResponse<ClippingJobResult>>(`/api/clipping/jobs?projectId=${projectId}`),
        ])

        if (cancelled) return

        if (projectRes.status === 'fulfilled') {
          setProject(projectRes.value.data)
        }

        if (variantsRes.status === 'fulfilled') {
          setClipVariants(variantsRes.value.data.items)
        }

        // job 仍请求，但只用于 transcript + 配置（videoUrl / captionStyle 等）
        if (jobRes.status === 'fulfilled') {
          setJob(jobRes.value.data)
        }

        setLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载失败')
          setLoading(false)
        }
      }
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  useEffect(() => {
    const templateId = project?.brandTemplateId ?? job?.brandTemplateId
    if (!templateId) {
      setBrandTemplate(null)
      return
    }

    let cancelled = false
    void appFetch<ApiResponse<Template>>(`/api/templates/${templateId}`)
      .then((res) => {
        if (!cancelled) {
          setBrandTemplate(res.data)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBrandTemplate(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [project?.brandTemplateId, job?.brandTemplateId])

  // 从 clip variants 还原出 ClipCandidate[]，与 job 的 transcript 配合使用
  const clips: { clip: ClipCandidate; variant: Variant }[] = clipVariants.map((v) => ({
    clip: variantToClipCandidate(v),
    variant: v,
  }))

  const transcript = job?.transcript

  const sortedClips = [...clips].sort((a, b) => {
    if (sortBy === 'score') return b.clip.score - a.clip.score
    return a.clip.startMs - b.clip.startMs
  })

  // 配置从 job 持久化数据读取
  const clipConfig: ClipRenderConfig = {
    src: job?.videoUrl ?? '',
    style: (brandTemplate?.config?.captionStyleKey as string | undefined) ?? job?.captionStyle,
    orientation: job?.orientation,
    resolution: job?.resolution,
    brandTemplateId: brandTemplate?.id ?? project?.brandTemplateId ?? job?.brandTemplateId ?? null,
    brandTemplateName: brandTemplate?.templateName ?? job?.brandTemplateName ?? null,
    captionConfig: (brandTemplate?.config?.captions as Record<string, unknown> | undefined) ?? null,
    templateConfig: brandTemplate?.config ?? null,
  }

  const doEdit = useCallback(
    async (
      clip: ClipCandidate,
      sentences: TranscriptSentence[],
      variantId: number,
      words: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }> | undefined,
    ) => {
      setEditingVariantId(variantId)
      setEditBusy(true)
      setEditError(null)
      try {
        const wsStatus = await ensureClipWorkspaceRoot()
        if (!wsStatus.ok) {
          setPendingEdit({ clip, sentences, words, variantId })
          setWorkspacePickerOpen(true)
          setEditBusy(false)
          setEditingVariantId(null)
          return
        }
        const result = await buildClipProjectForEditor(clip, sentences, clipConfig, words, Number(projectId))
        await navigate({
          to: '/editor/$projectId',
          params: { projectId: result.projectId },
          search: { assistant: true },
        })
      } catch (err) {
        setEditError(err instanceof Error ? err.message : '打开编辑器失败')
        setEditBusy(false)
        setEditingVariantId(null)
      }
    },
    [clipConfig, navigate],
  )

  const handleWorkspacePicked = useCallback(() => {
    if (pendingEdit) {
      void doEdit(pendingEdit.clip, pendingEdit.sentences, pendingEdit.variantId, pendingEdit.words)
      setPendingEdit(null)
    }
  }, [pendingEdit, doEdit])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          正在加载项目…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background px-5 py-10">
        <Link to="/app/projects" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-5" />返回
        </Link>
        <p className="mt-6 text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 顶栏 */}
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-5 backdrop-blur">
        <Link to="/app/projects" className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="size-5" />
        </Link>
        <span className="text-sm font-semibold text-foreground">{project?.title ?? '项目详情'}</span>
      </div>

      {/* 项目信息栏 */}
      <div className="mx-auto mt-6 flex max-w-[724px] flex-wrap items-start justify-between gap-2.5 rounded-2xl border border-border bg-card px-3 py-3 shadow-md sm:gap-6 sm:px-5">
        <div className="flex w-full items-start gap-3 sm:max-w-[65%]">
          <div className="flex h-[65px] w-[107px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
            {project?.coverUrl ? (
              <img src={resolveSourceUrl(project.coverUrl)} alt={project?.title ?? ''} className="size-full object-cover" />
            ) : (
              <Clapperboard className="size-6 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="truncate text-sm font-medium text-foreground">
              {project?.title ?? '视频片段'}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Globe2 className="size-3" />
                {transcript?.language ?? project?.primaryLanguage ?? '—'}
              </span>
              {transcript?.durationMs ? (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {formatDuration(transcript.durationMs)}
                </span>
              ) : null}
              <span className="flex items-center gap-1">
                <Layers className="size-3" />
                {clips.length} 个片段
              </span>
              {clipConfig.brandTemplateName ? (
                <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-foreground/80">
                  模板: {clipConfig.brandTemplateName}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* 工具栏 */}
      {clips.length > 0 ? (
        <div className="mx-auto mt-6 flex max-w-[820px] items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">全部片段 ({clips.length})</span>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'score' | 'time')}
            className="h-8 appearance-none rounded-full border border-border bg-card px-3 pr-7 text-xs font-medium text-muted-foreground focus:outline-none"
          >
            <option value="score">按评分排序</option>
            <option value="time">按时间排序</option>
          </select>
        </div>
      ) : null}

      {/* 片段列表 */}
      <div className="mx-auto max-w-[820px] space-y-4 px-5 pb-20 pt-4">
        {sortedClips.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Clapperboard className="size-12 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {job?.error ?? '该项目暂无分片结果'}
            </p>
          </div>
        ) : (
          sortedClips.map(({ clip, variant }, index) => (
            <ClipCard
              key={variant.id}
              clip={clip}
              index={index}
              variant={variant}
              sentences={transcript?.sentences ?? []}
              words={transcript?.words}
              config={clipConfig}
              onEdit={doEdit}
              editBusy={editBusy && editingVariantId === variant.id}
              editError={pendingEdit?.variantId === variant.id ? editError : null}
            onPublish={(selectedClip, selectedVariant) => setPendingPublish({ clip: selectedClip, variant: selectedVariant })}
            />
          ))
        )}
      </div>

      {/* 底部操作 */}
      <div className="mx-auto max-w-[820px] px-5 pb-10">
        <Button variant="outline" className="w-full rounded-full" onClick={() => navigate({ to: '/app/projects' })}>
          返回项目列表
        </Button>
      </div>

      {/* 工作空间选择弹窗 */}
      <WorkspacePickerDialog
        open={workspacePickerOpen}
        onOpenChange={setWorkspacePickerOpen}
        onSuccess={handleWorkspacePicked}
      />
      <PublishClipDialog
        open={pendingPublish !== null}
        onOpenChange={(open) => { if (!open) setPendingPublish(null) }}
        title={pendingPublish?.clip.title ?? ''}
        reason={pendingPublish?.clip.reason}
        projectId={Number(projectId)}
        variantId={pendingPublish?.variant.id ?? null}
        generateShareVideo={async () => {
          if (!pendingPublish) throw new Error('未选择要发布的片段')
          const result = await renderClipToMp4(
            pendingPublish.clip,
            transcript?.sentences ?? [],
            clipConfig,
            { words: transcript?.words },
          )
          return result.blob
        }}
      />
      {editBusy ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4 shadow-xl">
            <Loader2 className="size-5 animate-spin text-primary" />
            <div><div className="text-sm font-medium text-foreground">正在创建独立编辑项目</div><div className="mt-0.5 text-xs text-muted-foreground">导入素材并应用品牌模板…</div></div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
